import type { ConnectionCatalogItem, Tool } from "@rakazo/contracts";

export const FEATURED_CONNECTOR_IDS = [
  "gmail",
  "google-calendar",
  "google-drive",
  "slack",
  "notion",
] as const;

/** Business apps a company's setup can suggest; matched in any managed catalog by alias. */
const BUSINESS_CONNECTOR_IDS = ["outlook", "sharepoint", "quickbooks", "xero"] as const;

export type FeaturedConnectorId =
  | (typeof FEATURED_CONNECTOR_IDS)[number]
  | (typeof BUSINESS_CONNECTOR_IDS)[number];

const ALL_CONNECTOR_IDS: readonly FeaturedConnectorId[] = [
  ...FEATURED_CONNECTOR_IDS,
  ...BUSINESS_CONNECTOR_IDS,
];

export const FEATURED_CONNECTOR_LABELS: Record<FeaturedConnectorId, string> = {
  gmail: "Gmail",
  "google-calendar": "Google Calendar",
  "google-drive": "Google Drive",
  slack: "Slack",
  notion: "Notion",
  outlook: "Outlook",
  sharepoint: "SharePoint",
  quickbooks: "QuickBooks",
  xero: "Xero",
};

const FEATURED_ALIASES: Record<FeaturedConnectorId, readonly string[]> = {
  gmail: ["gmail", "googlemail", "google mail"],
  "google-calendar": ["googlecalendar", "google calendar", "google_calendar", "gcal"],
  "google-drive": ["googledrive", "google drive", "google_drive", "gdrive"],
  slack: ["slack", "slackbot"],
  notion: ["notion", "notion.so"],
  outlook: ["outlook", "microsoft outlook", "outlook365"],
  sharepoint: ["sharepoint", "share_point", "microsoft sharepoint"],
  quickbooks: ["quickbooks", "quickbooks online", "qbo"],
  xero: ["xero"],
};

/** The catalog apps behind each tool a company can pick at setup; tools with none are left out. */
const TOOL_CONNECTORS: Partial<Record<Tool, readonly FeaturedConnectorId[]>> = {
  microsoft365: ["outlook"],
  sharepoint: ["sharepoint"],
  google_workspace: ["gmail", "google-calendar"],
  google_drive: ["google-drive"],
  quickbooks: ["quickbooks"],
  xero: ["xero"],
  slack: ["slack"],
  notion: ["notion"],
};

/** The tools setup can connect, in the order of their groups. */
export function connectableTools(tools: readonly Tool[]): Tool[] {
  return tools.filter((tool) => TOOL_CONNECTORS[tool]);
}

/** What setup offers to connect first when the company named none of its tools. */
export const DEFAULT_FIRST_APPS: readonly Tool[] = [
  "microsoft365",
  "sharepoint",
  "google_workspace",
  "quickbooks",
  "xero",
];

/** The apps to offer first for a company's tools: those a catalog can connect, else the defaults. */
export function firstAppsForTools(tools: readonly Tool[] | undefined): Tool[] {
  const connectable = connectableTools(tools ?? []);
  return connectable.length ? connectable : [...DEFAULT_FIRST_APPS];
}

/** The catalog tiles for a list of tools, in order and without repeats. */
export function connectorsForTools(tools: readonly Tool[]): FeaturedConnectorId[] {
  return [...new Set(tools.flatMap((tool) => TOOL_CONNECTORS[tool] ?? []))];
}

/** Whether one of a tool's catalog apps is among the connected providers. */
export function toolConnected(tool: Tool, providers: readonly string[]): boolean {
  const ids = TOOL_CONNECTORS[tool] ?? [];
  return providers.some((provider) => {
    const id = matchFeaturedConnectorId(provider);
    return id !== null && ids.includes(id);
  });
}

/** Known tile ids from untrusted input such as a URL, in order and without repeats. */
export function parseConnectorIds(values: readonly string[]): FeaturedConnectorId[] {
  return [
    ...new Set(
      values.filter((value): value is FeaturedConnectorId =>
        (ALL_CONNECTOR_IDS as readonly string[]).includes(value),
      ),
    ),
  ];
}

export type FeaturedConnectorTile = {
  id: FeaturedConnectorId;
  label: string;
  item?: ConnectionCatalogItem;
  /** Catalog has items but none matched this featured connector. */
  missing: boolean;
};

/** Keep catalog screens responsive even when a provider exposes thousands of apps. */
export const CONNECTION_CATALOG_PAGE_SIZE = 60;

function normalizeConnectorKey(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, "");
}

export function matchFeaturedConnectorId(value: string): FeaturedConnectorId | null {
  const normalized = normalizeConnectorKey(value);
  if (!normalized) return null;
  for (const id of ALL_CONNECTOR_IDS) {
    if (FEATURED_ALIASES[id].some((alias) => normalizeConnectorKey(alias) === normalized)) {
      return id;
    }
  }
  return null;
}

export function featuredConnectorProvidersMatch(left: string, right: string): boolean {
  if (left === right) return true;
  const leftId = matchFeaturedConnectorId(left);
  const rightId = matchFeaturedConnectorId(right);
  return leftId !== null && leftId === rightId;
}

export function resolveFeaturedCatalogItem(
  id: FeaturedConnectorId,
  catalog: readonly ConnectionCatalogItem[],
): ConnectionCatalogItem | undefined {
  for (const item of catalog) {
    if (matchFeaturedConnectorId(item.slug) === id) return item;
    if (matchFeaturedConnectorId(item.name) === id) return item;
  }
  return undefined;
}

export function buildFeaturedConnectorTiles(
  catalog: readonly ConnectionCatalogItem[],
  ids: readonly FeaturedConnectorId[] = FEATURED_CONNECTOR_IDS,
): FeaturedConnectorTile[] {
  const hasCatalog = catalog.length > 0;
  return ids.map((id) => {
    const item = hasCatalog ? resolveFeaturedCatalogItem(id, catalog) : undefined;
    return {
      id,
      label: FEATURED_CONNECTOR_LABELS[id],
      item,
      missing: hasCatalog && !item,
    };
  });
}

/** The catalog under the featured tiles: with no query, every app the tiles do not already show. */
export function filterConnectionCatalogItems(
  catalog: readonly ConnectionCatalogItem[],
  query: string,
  featured: readonly FeaturedConnectorId[] = FEATURED_CONNECTOR_IDS,
): ConnectionCatalogItem[] {
  const needle = query.trim().toLowerCase();
  if (!needle) {
    const shown = (value: string) => {
      const id = matchFeaturedConnectorId(value);
      return id !== null && featured.includes(id);
    };
    return catalog.filter((item) => !shown(item.slug) && !shown(item.name));
  }
  return catalog.filter(
    (item) =>
      item.name.toLowerCase().includes(needle) ||
      item.slug.toLowerCase().includes(needle) ||
      item.connectorId.toLowerCase().includes(needle),
  );
}

export const EMPTY_PLUGIN_CATALOG_MESSAGE =
  "Configure a plugin catalog on the server to connect apps.";
